import React from 'react'
import { Card, CardBody, Row, Col, Button } from 'reactstrap'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Section from '../Section'
import Error from '../SvgIcons/Error'
import SimpleLoader from '../SimpleLoader'
import { checkPermission } from '../../utils/Auth/AuthService'
import {
    sendErrorReportEmail,
    toggleSystemStatusLoading,
} from '../../store/system/actions'
import { AppDispatch } from '../../configureStore'
import { IUserState } from '../../store/user/types'
import { navigateBackOrFallback } from '../../helpers/navigation'

interface IProps {
    error?: string
    errorEmailSent?: boolean
    createTicketFromError?: (error: string) => void | Promise<unknown>
    loading?: boolean
    user: Pick<IUserState, 'permissions'>
    dispatch?: AppDispatch
    errorId?: string
}

const extractErrorId = (error?: string): string => {
    if (!error) {
        return ''
    }
    const match = error.match(/Reference:\s*([A-Za-z0-9-]+)/i)
    return match ? match[1] : ''
}

export const ErrorGenericBase: React.FC<IProps> = ({
    error = '',
    loading,
    createTicketFromError,
    user,
    dispatch,
    errorEmailSent = false,
    errorId,
}) => {
    const navigate = useNavigate()
    const resolvedErrorId = errorId || extractErrorId(error)

    const goBack = () => navigateBackOrFallback(navigate)

    const reportIssue = () => {
        if (resolvedErrorId && dispatch) {
            dispatch(toggleSystemStatusLoading())
            dispatch(sendErrorReportEmail(resolvedErrorId)).then(() => {
                dispatch(toggleSystemStatusLoading())
            })
            return
        }

        if (createTicketFromError) {
            createTicketFromError(error)
        }
    }

    const canCreateTicket = checkPermission('cases.write', user.permissions)
    const canReportById = Boolean(resolvedErrorId)
    const canReport =
        canReportById || (canCreateTicket && Boolean(createTicketFromError))

    return (
        <Row className="d-flex justify-content-center">
            <Col md={8}>
                <Card className="mb-0">
                    <CardBody>
                        <div className="d-flex justify-content-center align-items-center py-4">
                            <Col className="d-flex align-items-end flex-column">
                                <Section title="Error">
                                    <SimpleLoader loading={loading || false}>
                                        <p>{error}</p>
                                        {resolvedErrorId ? (
                                            <p
                                                className="mb-2"
                                                data-testid="error-id-copy"
                                            >
                                                If you contact support, quote
                                                this ID:{' '}
                                                <strong>
                                                    {resolvedErrorId}
                                                </strong>
                                                .
                                            </p>
                                        ) : null}
                                        {errorEmailSent ? (
                                            <p className="text-success mb-2">
                                                Your error report has been
                                                submitted.
                                            </p>
                                        ) : null}
                                        <div className="align-items-start d-flex">
                                            {canReport &&
                                            (createTicketFromError ||
                                                resolvedErrorId) ? (
                                                <Button
                                                    onClick={reportIssue}
                                                    color="primary"
                                                    className="mr-2"
                                                    data-testid="report-issue-button"
                                                >
                                                    Report issue
                                                </Button>
                                            ) : null}
                                            <Button
                                                onClick={goBack}
                                                color="secondary"
                                                outline
                                                data-testid="error-back-button"
                                            >
                                                Back
                                            </Button>
                                        </div>
                                    </SimpleLoader>
                                </Section>
                            </Col>
                            <Col className="d-flex justify-content-center">
                                <Error
                                    cssClassTop="error-icon--top"
                                    cssClass="error-icon"
                                />
                            </Col>
                        </div>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    )
}
function mapStateToProps({ user }) {
    return {
        user,
    }
}
export default connect(mapStateToProps)(ErrorGenericBase)
